// メインのゲームシーン：森エントランスを開いてプレイヤーと敵を配置
import Phaser from 'phaser';
import { Player, defineWalkAnimations } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { Slime } from '../entities/enemies/Slime';
import { Goblin } from '../entities/enemies/Goblin';
import { Archer } from '../entities/enemies/Archer';
import { Boss } from '../entities/enemies/Boss';
import { Chest } from '../world/Chest';
import { buildTilemap, DISPLAY_TILE } from '../world/Tilemap';
import { forestMap, FOREST_SPAWNS } from '../world/maps/forest';
import { CombatSystem } from '../combat/CombatSystem';
import { SoundManager } from '../systems/SoundManager';
import { bus } from '../systems/EventBus';

const ARROW_LIFETIME_MS = 1600;

export class WorldScene extends Phaser.Scene {
  private player!: Player;
  private enemies!: Phaser.Physics.Arcade.Group;
  private arrows!: Phaser.Physics.Arcade.Group;
  private combat!: CombatSystem;
  private sound2!: SoundManager;
  private chests: Chest[] = [];

  constructor() {
    super('WorldScene');
  }

  create(): void {
    bus.removeAllListeners();
    this.chests = [];

    // アニメ定義（シーン作成時に1回）
    defineWalkAnimations(this);
    Slime.defineAnimations(this);
    Goblin.defineAnimations(this);
    Archer.defineAnimations(this);
    Boss.defineAnimations(this);

    // タイルマップ
    const map = buildTilemap(this, forestMap);
    this.physics.world.setBounds(0, 0, map.widthPx, map.heightPx);

    // プレイヤー
    const ps = FOREST_SPAWNS.player;
    this.player = new Player(
      this,
      ps.tx * DISPLAY_TILE + DISPLAY_TILE / 2,
      ps.ty * DISPLAY_TILE + DISPLAY_TILE / 2,
    );

    // 敵グループ
    this.enemies = this.physics.add.group({ runChildUpdate: true });
    for (const def of FOREST_SPAWNS.enemies) {
      const px = def.tx * DISPLAY_TILE + DISPLAY_TILE / 2;
      const py = def.ty * DISPLAY_TILE + DISPLAY_TILE / 2;
      let e: Enemy;
      if (def.kind === 'slime') {
        e = new Slime(this, px, py);
      } else if (def.kind === 'goblin') {
        e = new Goblin(this, px, py);
      } else {
        e = new Archer(this, px, py, {
          spawnArrow: (x, y, vx, vy) => this.spawnArrow(x, y, vx, vy),
        });
      }
      this.enemies.add(e);
    }
    // ボス
    const bs = FOREST_SPAWNS.boss;
    const boss = new Boss(
      this,
      bs.tx * DISPLAY_TILE + DISPLAY_TILE / 2,
      bs.ty * DISPLAY_TILE + DISPLAY_TILE / 2,
    );
    this.enemies.add(boss);

    // 矢（敵→プレイヤー）
    this.arrows = this.physics.add.group();

    // 宝箱（1個だけ：森の中の隠し）
    const cs = FOREST_SPAWNS.chest;
    const chest = new Chest(
      this,
      cs.tx * DISPLAY_TILE + DISPLAY_TILE / 2,
      cs.ty * DISPLAY_TILE + DISPLAY_TILE / 2,
      'heart_piece',
    );
    this.chests.push(chest);

    // タイル壁衝突
    if (map.wallsLayer) {
      this.physics.add.collider(this.player, map.wallsLayer);
      this.physics.add.collider(this.enemies, map.wallsLayer);
      this.physics.add.collider(this.arrows, map.wallsLayer, (a) => {
        (a as Phaser.Physics.Arcade.Image).disableBody(true, true);
      });
    }
    // 宝箱の物理衝突（押せないように）
    chest.body && this.physics.add.collider(this.player, chest);

    // 接触ダメージ
    this.physics.add.overlap(this.player, this.enemies, (_p, e) => {
      const enemy = e as Enemy;
      if (!enemy.active) return;
      const dx = this.player.x - enemy.x;
      const dy = this.player.y - enemy.y;
      const len = Math.max(1, Math.hypot(dx, dy));
      const kbx = (dx / len) * 160;
      const kby = (dy / len) * 160;
      this.player.takeDamage(enemy.spec().contactDamage, kbx, kby);
      this.sound2?.play('damage');
    });
    // 矢ヒット
    this.physics.add.overlap(this.player, this.arrows, (_p, a) => {
      const arrow = a as Phaser.Physics.Arcade.Image;
      if (!arrow.active) return;
      const dx = this.player.x - arrow.x;
      const dy = this.player.y - arrow.y;
      const len = Math.max(1, Math.hypot(dx, dy));
      this.player.takeDamage(1, (dx / len) * 160, (dy / len) * 160);
      arrow.disableBody(true, true);
      this.sound2?.play('damage');
    });
    // 宝箱を開く（接触）
    this.physics.add.overlap(this.player, chest, () => {
      if (!chest.opened) {
        chest.open();
        // ハートピース取得 → 最大HP+2
        this.player.applyMaxHpUp(2);
        this.sound2?.play('chest');
      }
    });

    // 戦闘システム
    this.combat = new CombatSystem(this, this.player, this.enemies);
    this.sound2 = new SoundManager();

    // 攻撃時のSE
    this.input.keyboard!.on('keydown-SPACE', () => {
      if (!this.player.isAttacking && !this.player.isDashing) {
        // 攻撃開始は Player 側で判定済みなので、ここはSEだけ
        this.sound2.play('sword');
      }
    });

    // 敵キル時SE
    bus.on('enemy-killed', () => this.sound2.play('hit'));

    // プレイヤー死亡
    bus.on('player-died', () => {
      this.sound2.play('death');
      this.scene.pause();
      this.scene.launch('GameOverScene');
    });

    // ボス撃破で勝利
    const bossKilledCheck = () => {
      if (!boss.active) {
        bus.emit('boss-defeated', undefined);
      }
    };
    bus.on('enemy-killed', bossKilledCheck);
    bus.on('boss-defeated', () => {
      this.scene.pause();
      this.scene.launch('VictoryScene');
    });

    // カメラ：プレイヤー追従
    this.cameras.main.setBounds(0, 0, map.widthPx, map.heightPx);
    this.cameras.main.startFollow(this.player, true, 0.15, 0.15);
    this.cameras.main.setRoundPixels(true);

    // 初期HUD通知
    bus.emit('player-hp', { hp: this.player.hp, maxHp: this.player.maxHp });

    // ミュートトグル
    this.input.keyboard!.on('keydown-M', () => this.sound2.toggleMute());
  }

  override update(time: number, delta: number): void {
    this.player.update(time, delta);

    // 敵の AI 更新
    this.enemies.getChildren().forEach((go) => {
      const e = go as Enemy;
      if (!e.active) return;
      e.tick(this.player.x, this.player.y, delta);
    });

    // 戦闘当たり判定
    this.combat.update(delta);
  }

  private spawnArrow(x: number, y: number, vx: number, vy: number): void {
    // 暫定で剣スプライトを矢の代用に（あとで矢素材に置換可能）
    const arrow = this.arrows.create(x, y, 'dungeon', 0) as Phaser.Physics.Arcade.Image;
    arrow.setScale(2);
    arrow.setVelocity(vx, vy);
    arrow.setTint(0xffeeaa);
    this.time.delayedCall(ARROW_LIFETIME_MS, () => {
      if (arrow.active) arrow.disableBody(true, true);
    });
  }
}
